import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Button,
    Card,
    Empty,
    Input,
    Modal,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tabs,
    Tag,
    Typography,
    notification,
} from 'antd';
import { AppstoreOutlined, GlobalOutlined, KeyOutlined, SearchOutlined, ShopOutlined, TagsOutlined } from '@ant-design/icons';
import { useAppSelector } from '../store/hooks';
import { rebuildBrandCatalog } from '../api/rebuildBrandCatalog';
import { assignBrandKeywordsToCustomProducts } from '../api/assignBrandKeywordsToCustomProduct';
import {
    deleteBrandKeywordResearch,
    generateBrandKeywordResearch,
    generateBrandKeywordResearchForAll,
} from '../api/generateBrandKeywordResearch';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { JobModel } from '../types/jobs';
import {
    BrandCatalogEntry,
    BrandCatalogLocaleSummary,
    BrandCatalogProductType,
    BrandResearchEntry,
    BrandResearchKeyword,
} from '../types/brandResearch';
import dayjs from 'dayjs';
import BrandResearchHistoryModal from '../components/BrandResearch/BrandResearchHistoryModal';
import WorkspaceModal from '../components/Layout/WorkspaceModal';
import { filterBrandRiskNegativeKeywords } from '../utils/brandResearchKeywords';
import './BrandResearchPage.css';

type BrandTableRow = {
    key: string;
    rank: number;
    brand: string;
    normalizedName: string;
    aliases: string[];
    source: string[];
    productCount: number;
    productTypes: BrandCatalogProductType[];
    initial: string;
    researchDocs: BrandResearchEntry[];
    latestResearch: BrandResearchEntry | null;
};

const statusColorMap: Record<string, string> = {
    COMPLETED: 'green',
    FAILED: 'red',
    IN_PROGRESS: 'blue',
    PARTIAL_SUCCESS: 'gold',
    PENDING: 'default',
};

const formatTimestamp = (value?: { toDate?: () => Date } | null): string => {
    if (!value || typeof value.toDate !== 'function') return '—';
    return dayjs(value.toDate()).format('YYYY-MM-DD HH:mm');
};

const formatLocaleLabel = (locale: BrandCatalogLocaleSummary): string => {
    return `${locale.marketplace.toUpperCase()} · ${locale.language || 'n/a'} (${locale.count})`;
};

const formatProductTypeLabel = (productType: BrandCatalogProductType): string => {
    const taxonomyPath = [
        productType.categoryDisplayName,
        productType.subcategoryDisplayName,
        productType.displayName,
    ].filter(Boolean).join(' > ');
    return `${taxonomyPath || productType.displayName} (${productType.count})`;
};

const intentTypeLabels: Record<string, string> = {
    category_anchor: 'Category Anchors',
    brand_category: 'Brand + Category',
    feature: 'Features',
    finish_material_style: 'Finish / Material / Style',
    use_case_audience: 'Use Case / Audience',
    commercial_modifier: 'Commercial Modifiers',
    negative_exclusion: 'Negative Exclusions',
};

const positiveIntentOrder = [
    'category_anchor',
    'brand_category',
    'feature',
    'finish_material_style',
    'use_case_audience',
    'commercial_modifier',
];

const renderKeywordTags = (keywords: BrandResearchKeyword[], limit = 5) => {
    if (!keywords.length) {
        return <Typography.Text type="secondary">—</Typography.Text>;
    }

    return (
        <Space size={[4, 8]} wrap>
            {keywords.slice(0, limit).map((keyword) => (
                <Tag key={`${keyword.keyword}-${(keyword.sources || []).join('-')}`} color="purple">
                    {keyword.keyword}
                    {typeof keyword.searchVolume === 'number' ? ` (${keyword.searchVolume})` : ''}
                </Tag>
            ))}
            {keywords.length > limit && <Tag>+{keywords.length - limit} more</Tag>}
        </Space>
    );
};

const renderGroupedKeywordTags = (keywords: BrandResearchKeyword[]) => {
    if (!keywords.length) {
        return <Typography.Text type="secondary">—</Typography.Text>;
    }

    const groups = keywords.reduce<Record<string, BrandResearchKeyword[]>>((acc, keyword) => {
        const intentType = keyword.intentType || 'category_anchor';
        acc[intentType] = [...(acc[intentType] || []), keyword];
        return acc;
    }, {});

    const orderedIntentTypes = [
        ...positiveIntentOrder.filter((intentType) => groups[intentType]?.length),
        ...Object.keys(groups).filter((intentType) => !positiveIntentOrder.includes(intentType)),
    ];

    return (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {orderedIntentTypes.map((intentType) => (
                <div key={intentType}>
                    <Typography.Text type="secondary">
                        {intentTypeLabels[intentType] || intentType}
                    </Typography.Text>
                    <div style={{ marginTop: 6 }}>
                        {renderKeywordTags(groups[intentType], 12)}
                    </div>
                </div>
            ))}
        </Space>
    );
};

const preferredKeywords = (record: BrandResearchEntry) => (
    record.canonicalKeywords?.length ? record.canonicalKeywords : record.priorityKeywords || []
);

const BrandResearchPage: React.FC = () => {
    const selectedCompany = useAppSelector((state) => state.companies.selectedCompany);
    const [rebuilding, setRebuilding] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [catalogEntries, setCatalogEntries] = useState<BrandCatalogEntry[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [researchEntries, setResearchEntries] = useState<BrandResearchEntry[]>([]);
    const [researchLoading, setResearchLoading] = useState(false);
    const [researchModalOpen, setResearchModalOpen] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedBrandRow, setSelectedBrandRow] = useState<BrandTableRow | null>(null);
    const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | undefined>();
    const [selectedLocaleKey, setSelectedLocaleKey] = useState<string | undefined>();
    const [submittingResearch, setSubmittingResearch] = useState(false);
    const [submittingAllResearch, setSubmittingAllResearch] = useState(false);
    const [assigningAllKeywords, setAssigningAllKeywords] = useState(false);
    const [deletingResearchId, setDeletingResearchId] = useState<string | null>(null);
    const [restartingResearchId, setRestartingResearchId] = useState<string | null>(null);
    const [activeResearchJobId, setActiveResearchJobId] = useState<string | null>(null);
    const [activeResearchJob, setActiveResearchJob] = useState<JobModel | null>(null);

    const normalizedSearch = searchValue.trim().toLowerCase();

    useEffect(() => {
        if (!selectedCompany?.id) {
            setCatalogEntries([]);
            setResearchEntries([]);
            return;
        }

        setCatalogLoading(true);
        setResearchLoading(true);

        const catalogQuery = query(
            collection(db, 'companies', selectedCompany.id, 'brandCatalog'),
            orderBy('displayName'),
        );

        const unsubscribe = onSnapshot(
            catalogQuery,
            (snapshot) => {
                const entries = snapshot.docs.map((doc) => {
                    const data = doc.data() as Omit<BrandCatalogEntry, 'id'>;
                    return {
                        id: doc.id,
                        ...data,
                    };
                });
                setCatalogEntries(entries);
                setCatalogLoading(false);
            },
            (error) => {
                console.error('Error fetching brand catalog:', error);
                notification.error({
                    message: 'Failed to load brand catalog',
                    description: 'There was an error loading the tracked brands for this company.',
                });
                setCatalogLoading(false);
            },
        );

        const researchUnsubscribe = onSnapshot(
            collection(db, 'companies', selectedCompany.id, 'brandResearch'),
            (snapshot) => {
                const entries = snapshot.docs.map((researchDoc) => ({
                    id: researchDoc.id,
                    ...(researchDoc.data() as Omit<BrandResearchEntry, 'id'>),
                }));
                setResearchEntries(entries);
                setResearchLoading(false);
            },
            (error) => {
                console.error('Error fetching brand research:', error);
                notification.error({
                    message: 'Failed to load brand research',
                    description: 'There was an error loading keyword research for this company.',
                });
                setResearchLoading(false);
            },
        );

        return () => {
            unsubscribe();
            researchUnsubscribe();
        };
    }, [selectedCompany?.id]);

    useEffect(() => {
        if (!selectedCompany?.id || !activeResearchJobId) {
            setActiveResearchJob(null);
            return;
        }

        const unsubscribe = onSnapshot(
            doc(db, 'companies', selectedCompany.id, 'jobs', activeResearchJobId),
            (snapshot) => {
                if (!snapshot.exists()) {
                    setActiveResearchJob(null);
                    return;
                }
                setActiveResearchJob({
                    id: snapshot.id,
                    ...(snapshot.data() as Omit<JobModel, 'id'>),
                });
            },
            (error) => {
                console.error('Error fetching brand research job:', error);
            },
        );

        return () => unsubscribe();
    }, [activeResearchJobId, selectedCompany?.id]);

    const researchByBrandKey = useMemo(() => {
        return researchEntries.reduce<Record<string, BrandResearchEntry[]>>((acc, entry) => {
            if (!acc[entry.brandKey]) {
                acc[entry.brandKey] = [];
            }
            acc[entry.brandKey].push(entry);
            return acc;
        }, {});
    }, [researchEntries]);

    const tableData = useMemo(() => {
        return catalogEntries
            .map((entry, index) => ({
                key: entry.id,
                rank: index + 1,
                brand: entry.displayName,
                normalizedName: entry.normalizedName,
                aliases: entry.aliases || [],
                source: entry.sources || [],
                productCount: entry.productCount || 0,
                productTypes: entry.productTypes || [],
                initial: entry.displayName.charAt(0).toUpperCase(),
                researchDocs: (researchByBrandKey[entry.normalizedName] || []).sort((left, right) => {
                    const leftTime = left.updatedAt?.toDate?.().getTime() || 0;
                    const rightTime = right.updatedAt?.toDate?.().getTime() || 0;
                    return rightTime - leftTime;
                }),
                latestResearch: null,
            }))
            .map((row) => ({
                ...row,
                latestResearch: row.researchDocs[0] || null,
            }))
            .filter((row) => {
                if (!normalizedSearch) return true;
                return (
                    row.brand.toLowerCase().includes(normalizedSearch)
                    || row.normalizedName.includes(normalizedSearch)
                    || row.aliases.some((alias) => alias.toLowerCase().includes(normalizedSearch))
                    || row.productTypes.some((productType) => (
                        productType.displayName.toLowerCase().includes(normalizedSearch)
                    ))
                    || row.researchDocs.some((researchDoc) => (
                        researchDoc.categoryDisplayName.toLowerCase().includes(normalizedSearch)
                        || preferredKeywords(researchDoc).some((keyword) => keyword.keyword.toLowerCase().includes(normalizedSearch))
                    ))
                );
            });
    }, [catalogEntries, normalizedSearch, researchByBrandKey]);

    const selectedHistoryRow = useMemo(() => {
        if (!selectedBrandRow) return null;
        return tableData.find((row) => row.key === selectedBrandRow.key) || selectedBrandRow;
    }, [selectedBrandRow, tableData]);

    const selectedProductType = useMemo(() => {
        if (!selectedBrandRow || !selectedCategoryKey) return null;
        return selectedBrandRow.productTypes.find((productType) => productType.normalizedName === selectedCategoryKey) || null;
    }, [selectedBrandRow, selectedCategoryKey]);

    const allResearchTargetCount = useMemo(() => {
        const targetKeys = new Set<string>();
        catalogEntries.forEach((entry) => {
            (entry.productTypes || []).forEach((productType) => {
                (productType.locales || []).forEach((locale) => {
                    if (!entry.normalizedName || !productType.normalizedName || !locale.marketplace || !locale.language) {
                        return;
                    }
                    targetKeys.add(`${entry.normalizedName}::${productType.normalizedName}::${locale.marketplace}::${locale.language}`);
                });
            });
        });
        return targetKeys.size;
    }, [catalogEntries]);

    const localeOptions = useMemo(() => {
        if (!selectedProductType?.locales?.length) return [];
        return selectedProductType.locales.map((locale) => ({
            label: formatLocaleLabel(locale),
            value: `${locale.marketplace}::${locale.language}`,
            locale,
        }));
    }, [selectedProductType]);

    const selectedLocale = useMemo(() => (
        localeOptions.find((option) => option.value === selectedLocaleKey)?.locale || null
    ), [localeOptions, selectedLocaleKey]);

    const selectedResearchTarget = useMemo(() => {
        if (!selectedHistoryRow || !selectedCategoryKey || !selectedLocaleKey) return null;
        const [marketplace, language] = selectedLocaleKey.split('::');
        return selectedHistoryRow.researchDocs.find((entry) => (
            entry.categoryKey === selectedCategoryKey
            && entry.marketplace === marketplace
            && entry.language === language
        )) || null;
    }, [selectedCategoryKey, selectedHistoryRow, selectedLocaleKey]);

    useEffect(() => {
        if (!selectedProductType) {
            setSelectedLocaleKey(undefined);
            return;
        }

        if ((selectedProductType.locales || []).length === 1) {
            const locale = selectedProductType.locales?.[0];
            setSelectedLocaleKey(locale ? `${locale.marketplace}::${locale.language}` : undefined);
            return;
        }

        setSelectedLocaleKey(undefined);
    }, [selectedProductType]);

    const columns = useMemo(() => ([
        {
            title: '#',
            dataIndex: 'rank',
            key: 'rank',
            width: 72,
            sorter: (a: BrandTableRow, b: BrandTableRow) => a.rank - b.rank,
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
            key: 'brand',
            sorter: (a: BrandTableRow, b: BrandTableRow) => a.brand.localeCompare(b.brand),
            render: (_: string, record: BrandTableRow) => (
                <Space size="middle">
                    <Avatar
                        style={{
                            background: 'linear-gradient(135deg, #1d4ed8 0%, #0f766e 100%)',
                            color: '#fff',
                            fontWeight: 700,
                        }}
                    >
                        {record.initial}
                    </Avatar>
                    <div>
                        <Typography.Text strong>{record.brand}</Typography.Text>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Research Key',
            dataIndex: 'normalizedName',
            key: 'normalizedName',
            sorter: (a: BrandTableRow, b: BrandTableRow) => a.normalizedName.localeCompare(b.normalizedName),
            render: (value: string) => (
                <Typography.Text code>{value}</Typography.Text>
            ),
        },
        {
            title: 'Aliases',
            dataIndex: 'aliases',
            key: 'aliases',
            render: (aliases: string[]) => (
                aliases.length === 0
                    ? <Typography.Text type="secondary">-</Typography.Text>
                    : (
                        <Space size={[4, 8]} wrap>
                            {aliases.map((alias) => (
                                <Tag key={alias}>{alias}</Tag>
                            ))}
                        </Space>
                    )
            ),
        },
        {
            title: 'Products',
            dataIndex: 'productCount',
            key: 'productCount',
            width: 110,
            sorter: (a: BrandTableRow, b: BrandTableRow) => a.productCount - b.productCount,
        },
        {
            title: 'Sources',
            dataIndex: 'source',
            key: 'source',
            width: 220,
            render: (values: string[]) => (
                <Space size={[4, 8]} wrap>
                    {values.map((value) => (
                        <Tag key={value} color={value === 'custom_upload' ? 'geekblue' : 'default'}>
                            {value}
                        </Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Research Docs',
            key: 'researchDocs',
            width: 120,
            sorter: (a: BrandTableRow, b: BrandTableRow) => a.researchDocs.length - b.researchDocs.length,
            render: (_: unknown, record: BrandTableRow) => (
                record.researchDocs.length > 0 ? (
                    <Button
                        type="link"
                        onClick={() => {
                            setSelectedBrandRow(record);
                            setHistoryModalOpen(true);
                        }}
                    >
                        {record.researchDocs.length}
                    </Button>
                ) : 0
            ),
        },
        {
            title: 'Latest Research',
            key: 'latestResearch',
            width: 240,
            render: (_: unknown, record: BrandTableRow) => (
                record.latestResearch ? (
                    <Space direction="vertical" size={4}>
                        <Button
                            type="link"
                            style={{ padding: 0, height: 'auto', justifyContent: 'flex-start' }}
                            onClick={() => {
                                setSelectedBrandRow(record);
                                setHistoryModalOpen(true);
                            }}
                        >
                            <Tag color={statusColorMap[record.latestResearch.status] || 'default'}>
                                {record.latestResearch.status}
                            </Tag>
                        </Button>
                        <Typography.Text type="secondary">
                            {formatTimestamp(record.latestResearch.updatedAt)}
                        </Typography.Text>
                    </Space>
                ) : (
                    <Typography.Text type="secondary">No research yet</Typography.Text>
                )
            ),
        },
        {
            title: 'Product Types',
            dataIndex: 'productTypes',
            key: 'productTypes',
            render: (productTypes: BrandCatalogProductType[]) => (
                productTypes.length === 0
                    ? <Typography.Text type="secondary">-</Typography.Text>
                    : (
                        <Space size={[4, 8]} wrap>
                            {productTypes.slice(0, 6).map((productType) => (
                                <Tag key={productType.normalizedName} color="gold">
                                    {productType.displayName} ({productType.count})
                                </Tag>
                            ))}
                            {productTypes.length > 6 && (
                                <Tag>+{productTypes.length - 6} more</Tag>
                            )}
                        </Space>
                    )
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            render: (_: unknown, record: BrandTableRow) => (
                <Button
                    type="link"
                    onClick={() => {
                        setSelectedBrandRow(record);
                        setSelectedCategoryKey(undefined);
                        setSelectedLocaleKey(undefined);
                        setActiveResearchJobId(null);
                        setResearchModalOpen(true);
                    }}
                    disabled={record.productTypes.length === 0}
                >
                    Research Keywords
                </Button>
            ),
        },
    ]), []);

    const expandedResearchColumns = useMemo(() => ([
        {
            title: 'Category',
            dataIndex: 'categoryDisplayName',
            key: 'categoryDisplayName',
        },
        {
            title: 'Locale',
            key: 'locale',
            render: (_: unknown, record: BrandResearchEntry) => (
                <Typography.Text code>
                    {record.marketplace.toUpperCase()} · {record.language || 'n/a'}
                </Typography.Text>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: unknown, record: BrandResearchEntry) => (
                <Tag color={statusColorMap[record.status] || 'default'}>
                    {record.status}
                </Tag>
            ),
        },
        {
            title: 'Top Keywords',
            key: 'priorityKeywords',
            render: (_: unknown, record: BrandResearchEntry) => renderGroupedKeywordTags(preferredKeywords(record)),
        },
        {
            title: 'Negative Keywords',
            key: 'negativeKeywords',
            render: (_: unknown, record: BrandResearchEntry) => (
                renderKeywordTags(filterBrandRiskNegativeKeywords(record.negativeKeywords), 4)
            ),
        },
        {
            title: 'Updated',
            key: 'updatedAt',
            render: (_: unknown, record: BrandResearchEntry) => formatTimestamp(record.updatedAt),
        },
    ]), []);

    const handleRebuildBrandCatalog = async () => {
        if (!selectedCompany?.id) {
            notification.error({
                message: 'No company selected',
                description: 'Please select a company before rebuilding the brand catalog.',
            });
            return;
        }

        try {
            setRebuilding(true);
            await rebuildBrandCatalog(selectedCompany.id);
            notification.success({
                message: 'Brand catalog rebuild started',
                description: 'The extraction is running in the background and will update the catalog shortly.',
            });
        } catch (error) {
            const description = error instanceof Error
                ? error.message
                : 'There was an error scheduling the brand extraction.';
            notification.error({
                message: 'Failed to rebuild brand catalog',
                description,
            });
        } finally {
            setRebuilding(false);
        }
    };

    const handleCloseResearchModal = () => {
        setResearchModalOpen(false);
        setSelectedBrandRow(null);
        setSelectedCategoryKey(undefined);
        setSelectedLocaleKey(undefined);
        setActiveResearchJobId(null);
    };

    const handleSubmitResearch = async () => {
        if (!selectedCompany?.id || !selectedBrandRow || !selectedCategoryKey || !selectedLocaleKey) {
            notification.error({
                message: 'Missing research inputs',
                description: 'Please select both a category and a locale before starting research.',
            });
            return;
        }

        const [marketplace, language] = selectedLocaleKey.split('::');

        try {
            setSubmittingResearch(true);
            const response = await generateBrandKeywordResearch({
                companyId: selectedCompany.id,
                brandKey: selectedBrandRow.normalizedName,
                categoryKey: selectedCategoryKey,
                marketplace,
                language,
            });
            setActiveResearchJobId(response.jobId);
            notification.success({
                message: 'Keyword research queued',
                description: 'The research job is running in the background and the table will update automatically.',
            });
        } catch (error) {
            const description = error instanceof Error
                ? error.message
                : 'There was an error scheduling keyword research.';
            notification.error({
                message: 'Failed to start keyword research',
                description,
            });
        } finally {
            setSubmittingResearch(false);
        }
    };

    const handleSubmitAllResearch = () => {
        if (!selectedCompany?.id) {
            notification.error({
                message: 'No company selected',
                description: 'Please select a company before starting keyword research.',
            });
            return;
        }

        Modal.confirm({
            title: 'Run Research for All Combinations',
            content: `Queue keyword research for ${allResearchTargetCount} brand, product type, and locale combinations? Existing research can be refreshed.`,
            okText: 'Queue research',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    setSubmittingAllResearch(true);
                    const result = await generateBrandKeywordResearchForAll(selectedCompany.id);

                    if (result.scheduledCount === 0) {
                        notification.warning({
                            message: 'No research queued',
                            description: result.message ?? 'No valid brand/product type combinations were ready for research.',
                        });
                        return;
                    }

                    notification.success({
                        message: 'Research queued',
                        description: `Queued ${result.scheduledCount} keyword research jobs${result.skippedCount ? ` and skipped ${result.skippedCount}` : ''}.`,
                    });
                } catch (error) {
                    const description = error instanceof Error
                        ? error.message
                        : 'There was an error scheduling keyword research.';
                    notification.error({
                        message: 'Failed to queue research',
                        description,
                    });
                } finally {
                    setSubmittingAllResearch(false);
                }
            },
        });
    };

    const handleAssignAllKeywords = () => {
        if (!selectedCompany?.id) {
            notification.error({
                message: 'No company selected',
                description: 'Please select a company before assigning product keywords.',
            });
            return;
        }

        Modal.confirm({
            title: 'Assign Keywords to All Products',
            content: `Queue keyword assignment for all products with completed brand research? This may take a while.`,
            okText: 'Queue assignments',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    setAssigningAllKeywords(true);
                    const result = await assignBrandKeywordsToCustomProducts(selectedCompany.id);

                    if (result.scheduledCount === 0) {
                        notification.warning({
                            message: 'No assignments queued',
                            description: result.message ?? 'No products had completed brand research ready for keyword assignment.',
                        });
                        return;
                    }

                    notification.success({
                        message: 'Assignments queued',
                        description: `Queued keyword assignment for ${result.scheduledCount} products${result.skippedCount ? ` and skipped ${result.skippedCount}` : ''}.`,
                    });
                } catch (error) {
                    const description = error instanceof Error
                        ? error.message
                        : 'There was an error scheduling product keyword assignment.';
                    notification.error({
                        message: 'Failed to queue assignments',
                        description,
                    });
                } finally {
                    setAssigningAllKeywords(false);
                }
            },
        });
    };

    const handleDeleteBrandResearch = async (entry: BrandResearchEntry) => {
        if (!selectedCompany?.id) {
            notification.error({
                message: 'No company selected',
                description: 'Please select a company before deleting keyword research.',
            });
            return;
        }

        try {
            setDeletingResearchId(entry.id);
            await deleteBrandKeywordResearch({
                companyId: selectedCompany.id,
                researchId: entry.id,
            });
            notification.success({
                message: 'Brand research deleted',
                description: 'The research document was removed. Existing product keywords were not changed.',
            });
        } catch (error) {
            const description = error instanceof Error
                ? error.message
                : 'There was an error deleting keyword research.';
            notification.error({
                message: 'Failed to delete brand research',
                description,
            });
        } finally {
            setDeletingResearchId(null);
        }
    };

    const handleRestartBrandResearch = async (entry: BrandResearchEntry) => {
        if (!selectedCompany?.id) {
            notification.error({
                message: 'No company selected',
                description: 'Please select a company before restarting keyword research.',
            });
            return;
        }

        try {
            setRestartingResearchId(entry.id);
            const response = await generateBrandKeywordResearch({
                companyId: selectedCompany.id,
                brandKey: entry.brandKey,
                categoryKey: entry.categoryKey,
                marketplace: entry.marketplace,
                language: entry.language,
            });
            setActiveResearchJobId(response.jobId);
            notification.success({
                message: 'Keyword research restarted',
                description: 'The refreshed research job is running in the background and this history will update automatically.',
            });
        } catch (error) {
            const description = error instanceof Error
                ? error.message
                : 'There was an error restarting keyword research.';
            notification.error({
                message: 'Failed to restart keyword research',
                description,
            });
        } finally {
            setRestartingResearchId(null);
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card
                    title="Brand Research"
                    extra={(
                        <Space wrap>
                            <Button
                                type="default"
                                icon={<TagsOutlined />}
                                onClick={handleRebuildBrandCatalog}
                                loading={rebuilding}
                                disabled={!selectedCompany?.id}
                            >
                                Extract Brands
                            </Button>
                            <Button
                                type="default"
                                icon={<SearchOutlined />}
                                onClick={handleSubmitAllResearch}
                                loading={submittingAllResearch}
                                disabled={!selectedCompany?.id || allResearchTargetCount === 0}
                            >
                                Research All
                            </Button>
                            <Button
                                type="primary"
                                icon={<KeyOutlined />}
                                onClick={handleAssignAllKeywords}
                                loading={assigningAllKeywords}
                                disabled={!selectedCompany?.id}
                            >
                                Assign All Keywords
                            </Button>
                        </Space>
                    )}
                >
                    <Typography.Paragraph>
                        This module is the home for your company-level brand and product-type research.
                        The underlying catalog is built from uploads and Shopify data so you can later identify the best terms to target.
                    </Typography.Paragraph>
                </Card>

                <Space size="large" style={{ width: '100%' }} wrap>
                    <Card style={{ minWidth: 220, flex: 1 }}>
                        <Statistic title="Tracked Brands" value={catalogEntries.length} />
                    </Card>
                    <Card style={{ minWidth: 220, flex: 1 }}>
                        <Statistic title="Visible In Table" value={tableData.length} />
                    </Card>
                    <Card style={{ minWidth: 220, flex: 1 }}>
                        <Statistic title="Research Docs" value={researchEntries.length} />
                    </Card>
                </Space>

                <Card title="Tracked Brands">
                    {catalogLoading || researchLoading ? (
                        <div style={{ padding: '32px 0', textAlign: 'center' }}>
                            <Spin />
                        </div>
                    ) : catalogEntries.length === 0 ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No tracked brands available yet. Run the extraction to build the catalog."
                        />
                    ) : (
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Input.Search
                                allowClear
                                placeholder="Search brands or research keys"
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                            <Table
                                rowKey="key"
                                columns={columns}
                                dataSource={tableData}
                                size="middle"
                                bordered
                                expandable={{
                                    rowExpandable: (record: BrandTableRow) => record.researchDocs.length > 0,
                                    expandedRowRender: (record: BrandTableRow) => (
                                        <Table
                                            rowKey="id"
                                            columns={expandedResearchColumns}
                                            dataSource={record.researchDocs}
                                            pagination={false}
                                            size="small"
                                        />
                                    ),
                                }}
                                pagination={{ pageSize: 8, hideOnSinglePage: true }}
                                locale={{
                                    emptyText: 'No brands match your current search.',
                                }}
                            />
                        </Space>
                    )}
                </Card>
            </Space>

            <WorkspaceModal
                open={researchModalOpen}
                className="brand-research-run-modal"
                title={(
                    <div className="brand-research-modal-title">
                        <Tag color="gold">Research</Tag>
                        <span className="brand-research-modal-title-main">
                            {selectedBrandRow?.brand || 'Brand Research'}
                        </span>
                        {selectedBrandRow && (
                            <span className="brand-research-modal-title-meta">
                                <span className="brand-research-modal-title-meta-item">
                                    <ShopOutlined />
                                    {selectedBrandRow.normalizedName}
                                </span>
                                <span className="brand-research-modal-title-meta-item">
                                    <AppstoreOutlined />
                                    {selectedBrandRow.productTypes.length} product types
                                </span>
                                {selectedLocale && (
                                    <span className="brand-research-modal-title-meta-item">
                                        <GlobalOutlined />
                                        {selectedLocale.marketplace.toUpperCase()} · {selectedLocale.language || 'n/a'}
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                )}
                footer={(
                    <div className="brand-research-modal-footer">
                        <Button onClick={handleCloseResearchModal}>
                            Close
                        </Button>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSubmitResearch}
                            disabled={!selectedCategoryKey || !selectedLocaleKey}
                            loading={submittingResearch}
                        >
                            {submittingResearch ? 'Starting' : 'Start Research'}
                        </Button>
                    </div>
                )}
                onClose={handleCloseResearchModal}
            >
                <div className="brand-research-modal-body">
                    <Tabs
                        className="brand-research-modal-tabs"
                        defaultActiveKey="setup"
                        items={[
                            {
                                key: 'setup',
                                label: 'Research Setup',
                                children: (
                                    <div className="brand-research-modal-scroll">
                                        {!selectedBrandRow ? (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description="Select a brand before starting keyword research."
                                            />
                                        ) : (
                                            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                                <div className="brand-research-modal-grid">
                                                    <section className="brand-research-panel brand-research-panel--setup">
                                                        <div className="brand-research-panel-header">
                                                            <span className="brand-research-panel-eyebrow">Research Target</span>
                                                            <Typography.Title level={4}>Keyword Scope</Typography.Title>
                                                        </div>

                                                        <div className="brand-research-field-stack">
                                                            <label className="brand-research-field">
                                                                <span>Brand</span>
                                                                <Input value={selectedBrandRow.brand} disabled />
                                                            </label>

                                                            <label className="brand-research-field">
                                                                <span>Product Type</span>
                                                                <Select
                                                                    showSearch
                                                                    optionFilterProp="label"
                                                                    placeholder="Select a product type"
                                                                    value={selectedCategoryKey}
                                                                    onChange={(value) => setSelectedCategoryKey(value)}
                                                                    options={(selectedBrandRow.productTypes || []).map((productType) => ({
                                                                        label: formatProductTypeLabel(productType),
                                                                        value: productType.normalizedName,
                                                                    }))}
                                                                />
                                                            </label>

                                                            <label className="brand-research-field">
                                                                <span>Marketplace & Language</span>
                                                                <Select
                                                                    showSearch
                                                                    optionFilterProp="label"
                                                                    placeholder="Select a locale"
                                                                    value={selectedLocaleKey}
                                                                    onChange={(value) => setSelectedLocaleKey(value)}
                                                                    options={localeOptions}
                                                                    disabled={!selectedCategoryKey}
                                                                />
                                                            </label>
                                                        </div>
                                                    </section>

                                                    <section className="brand-research-panel">
                                                        <div className="brand-research-panel-header">
                                                            <span className="brand-research-panel-eyebrow">Preview</span>
                                                            <Typography.Title level={4}>Selected Scope</Typography.Title>
                                                        </div>

                                                        <div className="brand-research-metric-grid">
                                                            <div className="brand-research-metric">
                                                                <span>Products</span>
                                                                <strong>{selectedProductType?.count ?? '—'}</strong>
                                                            </div>
                                                            <div className="brand-research-metric">
                                                                <span>Locales</span>
                                                                <strong>{selectedProductType?.locales?.length ?? '—'}</strong>
                                                            </div>
                                                            <div className="brand-research-metric">
                                                                <span>Existing Research</span>
                                                                <strong>{selectedResearchTarget ? 'Yes' : 'No'}</strong>
                                                            </div>
                                                        </div>

                                                        <div className="brand-research-scope-summary">
                                                            <div>
                                                                <Typography.Text type="secondary">Product Type</Typography.Text>
                                                                <div>
                                                                    {selectedProductType ? (
                                                                        <Typography.Text strong>
                                                                            {formatProductTypeLabel(selectedProductType)}
                                                                        </Typography.Text>
                                                                    ) : (
                                                                        <Typography.Text type="secondary">—</Typography.Text>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <Typography.Text type="secondary">Locale</Typography.Text>
                                                                <div>
                                                                    {selectedLocale ? (
                                                                        <Typography.Text code>
                                                                            {selectedLocale.marketplace.toUpperCase()} · {selectedLocale.language || 'n/a'}
                                                                        </Typography.Text>
                                                                    ) : (
                                                                        <Typography.Text type="secondary">—</Typography.Text>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <Typography.Text type="secondary">Last Research</Typography.Text>
                                                                <div>
                                                                    {selectedResearchTarget ? (
                                                                        <Space size={8} wrap>
                                                                            <Tag color={statusColorMap[selectedResearchTarget.status] || 'default'}>
                                                                                {selectedResearchTarget.status}
                                                                            </Tag>
                                                                            <Typography.Text type="secondary">
                                                                                {formatTimestamp(selectedResearchTarget.updatedAt)}
                                                                            </Typography.Text>
                                                                        </Space>
                                                                    ) : (
                                                                        <Typography.Text type="secondary">No matching research yet</Typography.Text>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </section>
                                                </div>

                                                {selectedProductType && (
                                                    <Alert
                                                        type="info"
                                                        showIcon
                                                        message="Research scope"
                                                        description={`This run will research ${selectedBrandRow.brand} in ${formatProductTypeLabel(selectedProductType)} for one locale.`}
                                                    />
                                                )}

                                                {activeResearchJob && (
                                                    <Alert
                                                        type={
                                                            activeResearchJob.status === 'FAILED'
                                                                ? 'error'
                                                                : activeResearchJob.status === 'COMPLETED'
                                                                    ? 'success'
                                                                    : 'info'
                                                        }
                                                        showIcon
                                                        message={`Job ${activeResearchJob.status}`}
                                                        description={
                                                            activeResearchJob.lastError
                                                                ? activeResearchJob.lastError
                                                                : 'The job has been queued. You can keep this modal open or close it and watch the table update.'
                                                        }
                                                    />
                                                )}
                                            </Space>
                                        )}
                                    </div>
                                ),
                            },
                            {
                                key: 'history',
                                label: 'Research History',
                                children: (
                                    <div className="brand-research-modal-scroll">
                                        {selectedHistoryRow?.researchDocs.length ? (
                                            <Table
                                                rowKey="id"
                                                columns={expandedResearchColumns}
                                                dataSource={selectedHistoryRow.researchDocs}
                                                pagination={{ pageSize: 8, hideOnSinglePage: true }}
                                                size="small"
                                                bordered
                                            />
                                        ) : (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description="No research documents found for this brand."
                                            />
                                        )}
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            </WorkspaceModal>
            <BrandResearchHistoryModal
                open={historyModalOpen}
                brandName={selectedHistoryRow?.brand}
                entries={selectedHistoryRow?.researchDocs || []}
                deletingEntryId={deletingResearchId}
                restartingEntryId={restartingResearchId}
                onDelete={handleDeleteBrandResearch}
                onRestart={handleRestartBrandResearch}
                onCancel={() => {
                    setHistoryModalOpen(false);
                    setSelectedBrandRow(null);
                }}
            />
        </div>
    );
};

export default BrandResearchPage;
